import React from 'react';

import { observer } from 'mobx-react';

import Record from 'ui/Record/Record';

import MainHeader from 'components/MainHeader';
import { Button } from 'components/Button';
import LibraryCategoriesDrawer from 'components/LibraryCategoriesDrawer';

import { EntityModules } from 'core/constants';
import routes from 'core/routes';

import { useNavBarHeight } from 'utils/hooks/navbar';
import { useStore } from 'utils/hooks/store';
import { canAddToLibrary, canManageLibraryCategories } from 'utils/roles/permissions';

import ControlsTable from './components/ControlsTable';
import CreateControlModal from './components/CreateControlModal';
import { HeaderActions } from './styles';

const Controls: React.FunctionComponent = (): React.ReactElement => {
  const navBarHeight = useNavBarHeight();
  const { session } = useStore();
  const [createOpen, setCreateOpen] = React.useState(false);
  const [categoriesOpen, setCategoriesOpen] = React.useState(false);
  const refreshRef = React.useRef<Function | null>(null);

  const onRefreshCallback = (callback: Function): void => {
    refreshRef.current = callback;
  };

  const listPath = routes.dashboard.risks.controls.toString();

  return (
    <>
      <Record
        Header={
          <MainHeader
            title="Controls Library"
            breadcrumbs={[
              { label: 'Risks', link: routes.dashboard.risks.toString() },
              { label: 'Controls library', link: listPath },
            ]}
            action={
              <HeaderActions>
                {canManageLibraryCategories(session.user) && (
                  <Button
                    variant="outlined"
                    size="large"
                    data-testid="controls-categories-button"
                    onClick={() => setCategoriesOpen(true)}
                  >
                    Manage categories
                  </Button>
                )}
                {canAddToLibrary(session.user) && !session.user?.readOnly && (
                  <Button
                    variant="contained"
                    size="large"
                    data-testid="controls-new-button"
                    onClick={() => setCreateOpen(true)}
                  >
                    Add control
                  </Button>
                )}
              </HeaderActions>
            }
          />
        }
        Content={(onError) => <ControlsTable onError={onError} onRefreshCallback={onRefreshCallback} />}
        navBarHeight={navBarHeight}
      />
      {createOpen && <CreateControlModal onClose={() => setCreateOpen(false)} />}
      {categoriesOpen && (
        <LibraryCategoriesDrawer
          entityType={EntityModules.Controls}
          onClose={(changed) => {
            setCategoriesOpen(false);
            if (changed) {
              refreshRef.current?.();
            }
          }}
        />
      )}
    </>
  );
};

export default observer(Controls);
